/**
 * Which pose a tracker dog is in, read from what the sim says it is doing (`Enemy.dog.mode`, ROUND8 §1) and how
 * fast it is going. The two tells the player reads are the head: `sniff` (nose to the ground) while it casts for
 * his trail or runs it, `bark` (head up, giving tongue) once it has him - or once it stands over its dead handler.
 *
 *   heel   walking with its handler, head up; standing with him a while, it sits
 *   cast   nose down, walking or standing                      - looking for his trail
 *   track  nose down at a trot                                  - on his trail
 *   bay    head up, barking, square to him or following him     - it HAS him
 *   mourn  sitting by its handler's body, barking
 */

import type { Enemy } from '../../sim/types';
import type { DogHead, DogMotion } from './dogPoses';

/** Faster than this over the ground it trots: a cast (1.5 m/s) walks, a hot trail (3.2 m/s) trots. */
export const TROT_ABOVE = 2.3;
/** Slower than this it is standing still, whatever `moving` says: a dog held up against a wall does not walk. */
export const MOVING_ABOVE = 0.25;
/** At heel and standing this long, seconds, it sits. */
export const SIT_AFTER = 2.5;

export interface DogChoice {
  motion: DogMotion;
  head: DogHead;
}

/**
 * A dead dog lies as a corpse. NOT 'down': the sim keeps every LIVING dog in state 'down', as it does the machines
 * (specials/dog.ts) - reading 'down' as fallen drew every live dog in the game as a corpse (found on screen when the
 * round-8 streams came together). 'wounded' is only ever the instant of a hit.
 */
export function dogDown(e: Pick<Enemy, 'state'>): boolean {
  return e.state === 'dead' || e.state === 'wounded';
}

/**
 * The pose for a live dog. `speed` is metres a second over the last tick; `stillFor` is seconds it has been
 * standing. An absent `dog` is a dog at heel (the sim's contract).
 */
export function chooseDogPose(e: Pick<Enemy, 'dog'>, speed: number, stillFor: number): DogChoice {
  const moving = speed > MOVING_ABOVE;
  const gait: DogMotion = !moving ? 'stand' : speed > TROT_ABOVE ? 'trot' : 'walk';
  switch (e.dog?.mode ?? 'heel') {
    case 'heel':
      return { motion: !moving && stillFor >= SIT_AFTER ? 'sit' : gait, head: 'alert' };
    case 'cast':
      return { motion: gait, head: 'sniff' };
    case 'track':
      // On a hot trail its nose stays down even when it checks, and it runs the line at a trot, never a walk.
      return { motion: moving ? 'trot' : 'stand', head: 'sniff' };
    case 'bay':
      return { motion: gait, head: 'bark' };
    case 'mourn':
      return { motion: moving ? gait : 'sit', head: 'bark' };
  }
}
